import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'
import last from 'lodash/last'
import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary from 'client/shared/decorators/error-boundary'
import isNull from 'lodash/isNull'
import { Helmet } from 'react-helmet-async'

import { authorsAsString } from 'client/bookmate/helpers/author/authorsAsString'
import {
  getResourceByIdGetter,
  getResourceOtherEpisodesLoader,
} from 'client/shared/helpers/resource-helpers'

import { getOtherEpisodes } from 'client/bookmate/selectors/series-selectors'

import urlFor from 'shared/tools/url-helper'

import MetaTitle from 'client/shared/blocks/meta-title'
import { Loader } from 'client/shared/blocks/loader'
import Spacer from 'client/shared/blocks/spacer'
import List from 'client/bookmate/blocks/list'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'

import { Book } from 'client/shared/blocks/book'
import AudioBook from 'client/bookmate/blocks/audiobook'
import { Comicbook } from 'client/shared/blocks/comicbook'

import { ResourceName, ResourceProps } from 'client/shared/types/resource'
import { Params } from 'client/shared/types/react-router'
import { Dispatch, State as ConnectedState } from 'shared/types/redux'

import { SeriesFullPartProps } from 'client/shared/types/series'
import { BookProps } from 'client/shared/types/book'
import { AudiobookProps } from 'client/shared/types/audiobook'
import { ComicbookProps } from 'client/shared/types/comicbook'
import { State as AppState } from 'client/shared/reducers/app-reducer'
import { sendOnClickAnalytics } from 'shared/tools/analytics-helper'

import { BOOK } from '../reducers/feed-entries-reducer'
import {
  AUDIOBOOK,
  COMICBOOK,
} from '../blocks/search-best-match/search-best-match'

type Props = {
  app: AppState
  otherEpisodes: []
  resource: ResourceProps
  resourceType: ResourceName
  page: number
  dispatch: Dispatch
  params: Params
  userId: number
} & withI18nProps

class ItemOtherEpisodesBox extends Component<Props> {
  static defaultProps = {
    otherEpisodes: [],
  }

  isEpisodes(): boolean {
    const { resourceType } = this.props

    return ['audiobook', 'comicbook'].includes(resourceType)
  }

  load = (): null => {
    const {
      dispatch,
      params: { uuid },
      page,
      resourceType,
    } = this.props
    const loadOtherEpisodes = getResourceOtherEpisodesLoader(resourceType)

    if (loadOtherEpisodes) {
      return dispatch(
        loadOtherEpisodes({ uuid, p: page, pp: 10, append: true }),
      )
    } else {
      return null
    }
  }

  render(): JSX.Element {
    const { page, otherEpisodes } = this.props

    const sortedEpisodes = [...otherEpisodes].sort(
      (min, max) => min.position_label - max.position_label,
    )

    return (
      <Fragment>
        {this.renderMeta()}
        {!sortedEpisodes.length ? (
          <div>
            <Spacer size={32} />
            <Loader color="gray" kind="big" centered />
          </div>
        ) : (
          <InfiniteScroll load={this.load} page={page}>
            <List>
              {sortedEpisodes.map((episode, index) =>
                this.renderOtherEpisodes(episode, index + 1),
              )}
            </List>
          </InfiniteScroll>
        )}
        <Spacer size={32} />
      </Fragment>
    )
  }

  renderOtherEpisodes(
    episode: SeriesFullPartProps,
    index: number,
  ): JSX.Element | null {
    switch (episode.resource_type) {
      case 'book':
        return this.renderBook(episode.resource, episode.position_label, index)
      case 'audiobook':
        return this.renderAudiobook(
          episode.resource,
          episode.position_label,
          index,
        )
      case 'comicbook':
        return this.renderComicbook(
          episode.resource,
          episode.position_label,
          index,
        )
      default:
        return null
    }
  }

  renderBook(
    book: BookProps,
    position: string,
    index: number,
  ): JSX.Element | null {
    const { resource, userId, dispatch } = this.props

    if (!book?.uuid || !resource) return null

    const seriesUuid = resource.series_list[0]?.uuid

    return (
      <Book
        onClick={() =>
          sendOnClickAnalytics({
            dispatch,
            resourceType: BOOK,
            itemUuid: book.uuid,
            screenName: `${BOOK}_other_episodes`,
            position: index,
            userId,
            resourceUuid: resource.uuid,
          })
        }
        book={book}
        kind="list"
        key={book.uuid}
        seriesPosition={position}
        seriesUuid={seriesUuid}
      />
    )
  }

  renderAudiobook(
    audiobook: AudiobookProps,
    position: string,
    index: number,
  ): JSX.Element | null {
    const { resource, userId, dispatch } = this.props

    if (!audiobook?.uuid || !resource) return null

    const seriesUuid = resource.series_list[0]?.uuid

    return (
      <AudioBook
        onClick={() =>
          sendOnClickAnalytics({
            dispatch,
            resourceType: AUDIOBOOK,
            itemUuid: audiobook.uuid,
            screenName: `${AUDIOBOOK}_other_episodes`,
            position: index,
            userId,
            resourceUuid: resource.uuid,
          })
        }
        audiobook={audiobook}
        kind="list"
        key={audiobook.uuid}
        seriesPosition={position}
        seriesUuid={seriesUuid}
        showPlus={isNull(audiobook.audio_card)}
      />
    )
  }

  renderComicbook(
    comicbook: ComicbookProps,
    position: string,
    index: number,
  ): JSX.Element | null {
    const { resource, userId, dispatch } = this.props

    if (!comicbook?.uuid || !resource) return null

    const seriesUuid = resource.series_list[0]?.uuid

    return (
      <Comicbook
        onClick={() =>
          sendOnClickAnalytics({
            dispatch,
            resourceType: COMICBOOK,
            itemUuid: comicbook.uuid,
            screenName: `${COMICBOOK}_other_episodes`,
            position: index,
            userId,
            resourceUuid: resource.uuid,
          })
        }
        comicbook={comicbook}
        kind="list"
        key={comicbook.uuid}
        seriesPosition={position}
        seriesUuid={seriesUuid}
      />
    )
  }

  renderMeta(): JSX.Element | null {
    const {
      app: { domain, query },
      resource: { authors, series_list, language },
      i18n,
    } = this.props

    if (!series_list || series_list.length === 0 || !authors) return null

    const { title, uuid } = series_list[0]

    const headTitleMessageId = this.isEpisodes()
      ? 'series.head_title_episodes'
      : 'series.head_title_books'
    const headTitle = i18n._(headTitleMessageId, {
      title,
      authors: authorsAsString(authors),
    })
    const canonicalLink =
      urlFor.absoluteRootWithSubdomain(language, domain) +
      urlFor.seriesEpisodes(uuid, query)

    return (
      <>
        <MetaTitle title={headTitle} />
        <Helmet link={[{ href: canonicalLink, rel: 'canonical' }]} />
      </>
    )
  }
}

const prepareComponentWrapper = prepareComponent(
  async function ({ store, params, router }) {
    const { uuid } = params
    const { dispatch } = store

    if (!uuid) return null
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { resourceType } = last(router.routes)
    const loadOtherEpisodes = getResourceOtherEpisodesLoader(resourceType)

    if (loadOtherEpisodes)
      await dispatch(loadOtherEpisodes({ uuid, p: 1, pp: 10 }))
  },
  {
    updateParams: 'uuid',
  },
)

const connectWrapper = connect(
  (
    state: ConnectedState,
    ownProps: { route: { resourceType: ResourceName } },
  ) => {
    const { resourceType } = ownProps.route
    const getItemById = getResourceByIdGetter(resourceType)

    return {
      app: state.app,
      otherEpisodes: getOtherEpisodes(state, resourceType),
      page: state[resourceType].pages?.otherEpisodes,
      resource: getItemById && getItemById(state, state[resourceType].uuid),
      resourceType,
      userId: state.currentUser.data.id,
    }
  },
)

const wrappers = compose(
  prepareComponentWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(ItemOtherEpisodesBox)
